// The app's dialog. One component, shared: the feature-request board opens in it, the
// request form opens in a second one stacked over the board, and the multi-ticket drafts
// work will reuse it. Nothing else in web/src was a dialog when this was written.
//
// What it guarantees, and where:
//
//   * role="dialog", aria-modal, and a label — the title, by aria-labelledby.
//   * Focus moves in on open (to `initialFocusRef` if given, else the dialog itself) and
//     goes back on close to whatever had it when the dialog opened — the opener. When an
//     outer dialog unmounts before the one above it, focus waits for the last one to
//     close and then goes to the outer opener (modal-stack.js, `close`).
//   * The page behind is INERT, not merely covered: modal-stack.js marks every other
//     top-level element of <body>, the dialog under this one included, so Tab and a
//     screen reader stay inside the innermost dialog. The page does not scroll behind an
//     open dialog either: overflow is hidden on <html> until the last one closes.
//   * Escape and a backdrop click close only the INNERMOST dialog. A click that starts
//     inside the panel and ends on the backdrop (a text selection dragged too far) does
//     not count; both the press and the release must be on the backdrop.
//   * Keys stop here. The dialog handles keydown itself and stops propagation, so nothing
//     typed in it reaches the window-level shortcuts of /tickets/new (Ctrl+Enter files the
//     ticket there; Alt+N opens the new-customer form). React's stopPropagation also
//     stops the native event, and React listens on the portal's container (<body>),
//     below window.
//
// Rendered into <body> through a portal, so it is never clipped by the page's layout and
// its backdrop is a direct child of <body> — which is what the inert bookkeeping walks.
//
// The parent decides whether it is open by rendering it or not; `onClose` is every way
// out (Escape, backdrop, the close button), so a form that must confirm before discarding
// does that in its own onClose.

import { useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { createModalStack } from './modal-stack.js';

// One stack for the whole app: dialogs stack over each other wherever they were rendered.
const stack = createModalStack({
  pageChildren: () => document.body.children,
  scrollRoot: () => document.documentElement,
});

// Written out as complete literals so Tailwind's source scanner sees them.
const WIDTHS = {
  md: 'max-w-lg',
  lg: 'max-w-3xl',
  full: 'max-w-[120rem]',
};

/**
 * @param {object} props
 * @param {string} props.title            the dialog's accessible name, shown as its heading
 * @param {() => void} props.onClose      Escape, backdrop and the close button all call it
 * @param {React.RefObject<HTMLElement>} [props.initialFocusRef]  where focus lands on open
 * @param {'md'|'lg'|'full'} [props.width]
 * @param {React.ReactNode} [props.actions]  extra controls in the header, before the close button
 */
export default function Modal({ title, onClose, initialFocusRef, width = 'md', actions, children }) {
  const titleId = useId();
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const pressedOnBackdrop = useRef(false);

  // Layout effect, so the page is inert and focus is inside before the first paint.
  // The opener is read here, BEFORE focus moves. On close the stack says where focus
  // goes, after it has un-inerted the layer below — focus() on an inert element does
  // nothing — or null when another dialog is still on top.
  useLayoutEffect(() => {
    const backdrop = backdropRef.current;
    stack.open(backdrop, document.activeElement);
    (initialFocusRef?.current ?? panelRef.current)?.focus();
    return () => {
      const returnTo = stack.close(backdrop);
      if (returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus();
    };
    // Mount and unmount only: a parent passing a fresh ref object must not re-run it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleKeyDown(event) {
    event.stopPropagation();
    if (event.key === 'Escape' && stack.isTop(backdropRef.current)) {
      event.preventDefault();
      onClose();
    }
  }

  function handleMouseDown(event) {
    pressedOnBackdrop.current = event.target === event.currentTarget;
  }

  function handleClick(event) {
    // Only a click whose target is this backdrop counts. Today no dialog is rendered inside
    // another's children (the request form is the board's sibling), but if one ever is, a
    // click in it would bubble here through React's tree (portals bubble by component, not
    // by DOM), and this check is what keeps it from closing the dialog below.
    const onBackdrop = event.target === event.currentTarget && pressedOnBackdrop.current;
    pressedOnBackdrop.current = false;
    if (onBackdrop && stack.isTop(backdropRef.current)) onClose();
  }

  return createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/60 p-2 sm:p-6"
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`w-full ${WIDTHS[width] ?? WIDTHS.md} rounded-lg bg-surface text-ink shadow-xl ring-1 ring-line focus:outline-none`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 id={titleId} className="text-base font-semibold text-ink">{title}</h2>
          <div className="flex items-center gap-2">
            {actions}
            <button type="button" className="btn-ghost px-2" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
